import type { TFunction } from 'i18next';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon, channelName, type IconName } from '../../components/icons';
import { Badge, LIST_ROW, Skeleton, StatusBadge } from '../../components/ui';
import { Avatar } from '../../components/ui/avatar';
import { ApiError, type AutomationAnalyticsSummary, type ChannelAccount, type ConversationSummary } from '../../lib/api';
import { formatPercent, formatRelative, formatWhen } from '../../lib/format';
import { successFigure } from '../analytics/overviewParts';
import { planBlockOf, planBlockText } from '../billing/plan';
import { contactName } from '../inbox/shared';
import type { ActivityLine, ActivityTone, RunChip } from './activity';

/** The Dashboard's building blocks (R01, `Dashboard.dc.html`; Murmura P1, `P1-Dashboard.dc.html`). */

const ROW_LINK = `${LIST_ROW} transition-colors hover:bg-surface-2/60 focus-visible:bg-surface-2/60`;

/** A card's error in the reader's language; a range past the plan's history (402, D-183) says so. */
export function errorMessage(err: unknown, t: TFunction): string {
  const block = planBlockOf(err);
  if (block) return planBlockText(block, t).body;
  return err instanceof ApiError ? err.message : t('dashboard.errorGeneric');
}

const LINK = 'flex items-center gap-1 whitespace-nowrap text-ds-sm font-semibold text-primary-700 hover:underline';

/** A card's link with its arrow: "Tout voir", "Ouvrir", "Voir les automatisations". */
export function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={`touch-target ${LINK}`}>
      {children}
      <Icon name="arrowRight" className="size-[13px]" />
    </Link>
  );
}

/** A card's rows on their way. */
export function RowsSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t('common.loading')} className="flex flex-col gap-3 px-5 py-4">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

// --- KPIs ------------------------------------------------------------------------------

/** "+12%" against the previous window; nothing when there was nothing before. */
export function percentDelta(current: number | undefined, previous: number | undefined): { text: string; positive: boolean } | null {
  if (current === undefined || previous === undefined || previous === 0) return null;
  const ratio = (current - previous) / previous;
  // A change that rounds to 0% reads as flat, not as "+0%" in green.
  if (Math.round(Math.abs(ratio) * 100) === 0) return { text: formatPercent(0, undefined, 0), positive: false };
  const sign = ratio > 0 ? '+' : '−';
  return { text: `${sign}${formatPercent(Math.abs(ratio), undefined, 0)}`, positive: ratio > 0 };
}

export interface StatLinkProps {
  to: string;
  icon: IconName;
  label: string;
  value: ReactNode;
  /** A quieter suffix on the value ("sur 6"). */
  unit?: ReactNode;
  /** Beside the value: the delta ("+1", "="). */
  badge?: ReactNode;
  /** After the label: what the figure means ("nouveaux sur la période"). */
  phrase?: ReactNode;
  /** A current state, not a figure of the period (Active automations): its disc is greyed. */
  state?: boolean;
  /** The icon's colour when the figure needs attention (the calendar's "Sans horaire", "Échec"); grey otherwise. */
  tone?: 'warning' | 'danger';
}

const ICON_TONES = { warning: 'text-warning-700', danger: 'text-danger-700' } as const;

/**
 * One stat of the strip (Murmura M1, `P1-Dashboard.dc.html` `.stat`): an icon
 * in a disc, the value with its delta, then "label · phrase". It opens the
 * page behind its figure, as the KPI cards did.
 */
export function StatLink({ to, icon, label, value, unit, badge, phrase, state = false, tone }: StatLinkProps) {
  return (
    <Link to={to} className="group flex min-w-0 items-center gap-2.5 rounded-ds-lg py-1 pe-2 transition-colors hover:bg-surface-2/60">
      <span
        aria-hidden
        className={`flex size-10 shrink-0 items-center justify-center rounded-full border border-border ${tone ? ICON_TONES[tone] : 'text-ink-2'} ${state ? 'bg-surface-2' : 'bg-surface'}`}
      >
        <Icon name={icon} className="size-[18px]" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="tabular text-[22px] font-bold leading-[26px] tracking-[-0.01em] text-ink">
            {value}
            {unit ? <span className="ms-1 text-ds-sm font-medium text-ink-3">{unit}</span> : null}
          </span>
          {badge}
        </span>
        <span className="text-ds-xs text-ink-3">
          <span>{label}</span>
          {phrase ? (
            <>
              {' · '}
              <span>{phrase}</span>
            </>
          ) : null}
        </span>
      </span>
    </Link>
  );
}

// --- Automation health ------------------------------------------------------------------

/** Failing ones first, then the busiest. */
export function healthOrder(a: AutomationAnalyticsSummary, b: AutomationAnalyticsSummary): number {
  return b.executionsFailed - a.executionsFailed || b.executionsTotal - a.executionsTotal;
}

/**
 * The badge is the success rate, never "enabled" (R01.3, A01-01): green when
 * every finished run succeeded, red once one failed, grey "—" with nothing to
 * measure. Under five runs it reads as a ratio ("2/2", GLOSSARY-FR §3).
 */
function HealthBadge({ automation: a }: { automation: AutomationAnalyticsSummary }) {
  const { t } = useTranslation();
  const figure = successFigure(a.executionsCompleted, a.executionsFailed);
  const tone = figure === null ? 'neutral' : a.executionsFailed > 0 ? 'err' : 'ok';
  return (
    <Badge tone={tone} className="min-w-16 justify-center">
      <span aria-hidden>{figure ?? '—'}</span>
      <span className="sr-only">{figure === null ? t('dashboard.health.noRate') : t('dashboard.health.rate', { value: figure })}</span>
    </Badge>
  );
}

/** What the dashboard knows about a row beyond the period's figures (critique 2026-10-29, dashboard P1 #4). */
export interface HealthNotes {
  /** When its latest run failed, from the activity log. */
  lastFailureAt?: string;
  /** Live, and nothing has started it for 30 days. */
  silent?: boolean;
}

function healthDetail(a: AutomationAnalyticsSummary, notes: HealthNotes, t: TFunction): string {
  if (a.executionsTotal === 0) return notes.silent ? t('dashboard.health.silent') : t('dashboard.health.none');
  const fired = t('dashboard.health.fired', { count: a.executionsTotal });
  if (a.executionsFailed > 0) {
    const last = notes.lastFailureAt ? ` · ${t('dashboard.health.lastFailed', { when: formatRelative(notes.lastFailureAt, { suffix: true, inSentence: true }) })}` : '';
    return `${fired} · ${t('dashboard.health.failed', { count: a.executionsFailed })}${last}`;
  }
  const running = a.executionsTotal - a.executionsCompleted;
  if (running > 0) return `${fired} · ${t('dashboard.health.inProgress', { count: running })}`;
  return `${fired} · ${t(a.executionsTotal === 1 ? 'dashboard.health.succeeded' : 'dashboard.health.allSucceeded')}`;
}

export function HealthRow({ automation, notes = {} }: { automation: AutomationAnalyticsSummary; notes?: HealthNotes }) {
  const { t } = useTranslation();
  const quiet = automation.executionsTotal === 0 && notes.silent;
  return (
    <Link to={`/analytics/automations/${automation.automationId}`} className={ROW_LINK}>
      <HealthBadge automation={automation} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span title={automation.name} className="truncate text-ds-base font-semibold text-ink">
          {automation.name}
        </span>
        <span className={`truncate text-ds-sm ${quiet ? 'font-medium text-warning-700' : 'text-ink-3'}`}>{healthDetail(automation, notes, t)}</span>
      </span>
    </Link>
  );
}

const DAY_MS = 86_400_000;
/** A connected account whose token runs out within this many days gets a warning. */
export const TOKEN_WARNING_DAYS = 7;

export function expiringAccounts(accounts: ChannelAccount[], now = Date.now()): ChannelAccount[] {
  return accounts.filter((account) => {
    if (account.status !== 'connected' || !account.tokenExpiresAt) return false;
    return Date.parse(account.tokenExpiresAt) - now < TOKEN_WARNING_DAYS * DAY_MS;
  });
}

export function TokenWarning({ account, now = Date.now() }: { account: ChannelAccount; now?: number }) {
  const { t } = useTranslation();
  const days = Math.ceil((Date.parse(account.tokenExpiresAt ?? '') - now) / DAY_MS);
  const names = { channel: channelName(account.channelCode), account: account.displayName };
  return (
    <div className="flex items-center gap-2 border-b border-border bg-warning-50 px-5 py-2.5 text-ds-sm text-warning-700">
      <Icon name="alert" className="size-[14px] shrink-0" />
      <span className="min-w-0 flex-1">
        {days <= 0 ? t('dashboard.token.expired', names) : t('dashboard.token.expires', { ...names, count: days })}
      </span>
      <Link to="/settings/channels" className="touch-target shrink-0 font-semibold underline underline-offset-2">
        {t('dashboard.token.renew')}
      </Link>
    </div>
  );
}

// --- Recent conversations ----------------------------------------------------------------

/** A preview that is only a link reads "Lien · uss.rnu.tn" instead of the raw URL (R01.5, A01-05). */
export function linkDomain(preview: string | null): string | null {
  const text = preview?.trim() ?? '';
  if (!/^https?:\/\/\S+$/i.test(text)) return null;
  try {
    return new URL(text).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

export function ConversationRow({ conversation }: { conversation: ConversationSummary }) {
  const { t } = useTranslation();
  const name = contactName(conversation.contact);
  const unread = conversation.unreadCount > 0;
  const domain = linkDomain(conversation.preview);
  return (
    <Link to={`/inbox/${conversation.id}`} className={ROW_LINK}>
      <Avatar name={name} seed={conversation.contact.id} src={conversation.contact.avatarUrl} channel={conversation.channel} size={28} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline justify-between gap-2">
          <span className={`truncate text-ds-base text-ink ${unread ? 'font-bold' : 'font-semibold'}`}>{name}</span>
          <span className="shrink-0 text-ds-xs text-ink-3">{conversation.lastMessageAt ? formatWhen(conversation.lastMessageAt) : ''}</span>
        </span>
        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1 text-ds-sm text-ink-2">
            {domain ? <Icon name="link" className="size-3 shrink-0" /> : null}
            <span className="truncate">{domain ? t('dashboard.conversations.link', { domain }) : (conversation.preview ?? t('dashboard.conversations.noMessages'))}</span>
          </span>
          {unread ? (
            <span className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary-600 px-[5px] text-[10.5px] font-bold text-white">
              <span aria-hidden>{conversation.unreadCount}</span>
              <span className="sr-only">{t('dashboard.conversations.unread', { count: conversation.unreadCount })}</span>
            </span>
          ) : null}
        </span>
      </span>
    </Link>
  );
}

// --- Recent activity --------------------------------------------------------------------

const ACTIVITY_TONE: Record<ActivityTone, string> = {
  accent: 'bg-primary-50 text-primary-700',
  positive: 'bg-success-50 text-success-700',
  caution: 'bg-warning-50 text-warning-700',
  neutral: 'bg-surface-3 text-ink-2',
};

export interface ActivityRowModel {
  id: string;
  at: string;
  /** The contact the row is about: their avatar leads the row; otherwise the event's icon does. */
  contact?: { id: string; name: string };
  line: ActivityLine;
  chips: RunChip[];
}

export function ActivityRow({ row }: { row: ActivityRowModel }) {
  return (
    <li className={LIST_ROW}>
      {row.contact ? (
        <Avatar name={row.contact.name} seed={row.contact.id} size={28} />
      ) : (
        <span aria-hidden className={`flex size-7 shrink-0 items-center justify-center rounded-full ${ACTIVITY_TONE[row.line.tone]}`}>
          <Icon name={row.line.icon} className="size-[14px]" />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-ds-base text-ink-2">
          {row.line.parts.map((part, i) =>
            part.strong ? (
              <b key={i} className="font-semibold text-ink">
                {part.text}
              </b>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        </span>
        {row.chips.length > 0 ? (
          <span className="flex flex-wrap gap-1.5">
            {row.chips.map((chip) =>
              chip.kind === 'status' ? (
                <StatusBadge key={chip.status} status={chip.status} />
              ) : (
                <Badge key={chip.label} tone={chip.tone}>
                  {chip.label}
                </Badge>
              ),
            )}
          </span>
        ) : null}
      </span>
      <span className="shrink-0 self-start whitespace-nowrap pt-0.5 text-ds-xs text-ink-3">{formatWhen(row.at)}</span>
    </li>
  );
}

// --- Setup checklist --------------------------------------------------------------------

export interface SetupStep {
  title: string;
  description: string;
  icon: IconName;
  to: string;
  done: boolean;
}

/** Until the workspace can run (a channel and an automation): the steps left, as links (Q3, R39). */
export function SetupChecklist({ workspaceName, steps }: { workspaceName: string; steps: SetupStep[] }) {
  const { t } = useTranslation();
  const headingId = useId();
  const done = steps.filter((s) => s.done).length;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4 rounded-ds-lg border border-border bg-surface p-5 shadow-ds-xs">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-ds-lg text-ink">
            {t('dashboard.setup.title', { workspace: workspaceName })}
          </h2>
          <p className="text-ds-sm text-ink-2">{t('dashboard.setup.lede')}</p>
        </div>
        <div className="flex min-w-[200px] flex-1 items-center gap-2.5 @min-[640px]:flex-none">
          <div
            role="progressbar"
            aria-label={t('dashboard.setup.progress')}
            aria-valuemin={0}
            aria-valuemax={steps.length}
            aria-valuenow={done}
            className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3 @min-[640px]:w-32"
          >
            <div className="h-full rounded-full bg-primary-600 transition-[width]" style={{ width: `${(done / steps.length) * 100}%` }} />
          </div>
          <span className="whitespace-nowrap text-ds-sm font-semibold text-ink-2">
            {t('dashboard.setup.done', { count: done, total: steps.length })}
          </span>
        </div>
      </div>
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-2.5">
        {steps.map((step) => (
          <li key={step.title}>
            <Link
              to={step.to}
              className={`flex h-full items-start gap-3 rounded-ds-md border p-3.5 transition-colors hover:border-primary-200 ${
                step.done ? 'border-border bg-surface-2' : 'border-border-strong bg-surface'
              }`}
            >
              <span
                aria-hidden
                className={`flex size-[30px] shrink-0 items-center justify-center rounded-ds-sm ${
                  step.done ? 'bg-success-50 text-success-700' : 'bg-primary-50 text-primary-700'
                }`}
              >
                <Icon name={step.done ? 'check' : step.icon} className="size-4" />
              </span>
              <span className="flex min-w-0 flex-col gap-[3px]">
                <span className={`text-ds-base font-semibold ${step.done ? 'text-ink-3 line-through' : 'text-ink'}`}>
                  {step.title}
                  {step.done ? <span className="sr-only"> {t('dashboard.setup.doneSr')}</span> : null}
                </span>
                <span className="text-ds-sm text-ink-3">{step.description}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
